import axios from 'axios';
import Cookies from 'js-cookie';
import api from './api';

/**
 * Finance Service API Client
 * Handles all finance-related API calls
 * Uses the main API instance for consistent authentication
 */

// Use the main API instance for consistent auth handling
const financeApi = api;

// =============================================================================
// ACCOUNTING MANAGEMENT APIs
// =============================================================================

export const accountingApi = {
  // Chart of Accounts
  getAccounts: (params = {}) => financeApi.get('/finance/accounting/accounts', { params }),
  createAccount: (data) => financeApi.post('/finance/accounting/accounts', data),

  // Journal Entries
  getJournalEntries: (params = {}) => financeApi.get('/finance/accounting/journal-entries', { params }),
  createJournalEntry: (data) => financeApi.post('/finance/accounting/journal-entries', data),

  // Financial Reports
  getTrialBalance: (params = {}) => financeApi.get('/finance/accounting/trial-balance', { params }),
  getFinancialStatements: (type, params = {}) => financeApi.get(`/finance/accounting/financial-statements/${type}`, { params }),
};

// =============================================================================
// ACCOUNT TYPES MANAGEMENT APIs
// =============================================================================

export const accountTypesApi = {
  // Get all account types
  getAccountTypes: (params = {}) => financeApi.get('/finance/account-types', { params }),

  // Get account types with their categories
  getAccountTypesWithCategories: (params = {}) =>
    financeApi.get('/finance/account-types/with-categories', { params }),

  // Get balance sheet account types
  getBalanceSheetTypes: () => financeApi.get('/finance/account-types/balance-sheet'),

  // Get income statement account types
  getIncomeStatementTypes: () => financeApi.get('/finance/account-types/income-statement'),

  // Get account type categories for specific account type
  getAccountTypeCategories: (accountType) =>
    financeApi.get(`/finance/account-types/${accountType}/categories`),

  // Single account type operations
  getAccountType: (id) => financeApi.get(`/finance/account-types/${id}`),
  createAccountType: (data) => financeApi.post('/finance/account-types', data),
  updateAccountType: (id, data) => financeApi.put(`/finance/account-types/${id}`, data),
  deleteAccountType: (id) => financeApi.delete(`/finance/account-types/${id}`),

  // Reorder account types
  reorderAccountTypes: (accountTypeIds) =>
    financeApi.put('/finance/account-types/reorder', { accountTypeIds }),
};

// =============================================================================
// CATEGORIES MANAGEMENT APIs
// =============================================================================

export const categoriesApi = {
  // Get all categories
  getCategories: (params = {}) => financeApi.get('/finance/categories', { params }),

  // Get categories grouped by account type (for Chart of Accounts)
  getCategoriesGrouped: () => financeApi.get('/finance/categories?groupBy=accountType'),

  // Get categories for specific account type (legacy method - now expects ObjectId)
  getCategoriesByType: (accountTypeId, includeInactive = false) =>
    financeApi.get(`/finance/categories?accountType=${accountTypeId}&includeInactive=${includeInactive}`),

  // Get categories by account type (new dedicated endpoint - expects ObjectId)
  getCategoriesByAccountType: (accountTypeId, params = {}) =>
    financeApi.get(`/finance/categories/by-account-type/${accountTypeId}`, { params }),

  // Get all categories grouped by account type (optimized endpoint)
  getAllCategoriesGrouped: (params = {}) =>
    financeApi.get('/finance/categories/grouped-by-account-type', { params }),

  // Single category operations
  getCategory: (id) => financeApi.get(`/finance/categories/${id}`),
  createCategory: (data) => financeApi.post('/finance/categories', data),
  updateCategory: (id, data) => financeApi.put(`/finance/categories/${id}`, data),
  deleteCategory: (id) => financeApi.delete(`/finance/categories/${id}`),

  // Preview category code before creation
  previewCategoryCode: (data) => financeApi.post('/finance/categories/preview-code', data),

  // Bulk operations
  bulkUpdate: (operation, categoryIds, data = {}) =>
    financeApi.post('/finance/categories/bulk', { operation, categoryIds, data }),

  // Reorder categories within account type (expects ObjectId)
  reorderCategories: (accountTypeId, categoryIds) =>
    financeApi.put(`/finance/categories/reorder/${accountTypeId}`, { categoryIds }),
};

// =============================================================================
// COST CENTERS MANAGEMENT APIs
// =============================================================================

export const costCentersApi = {
  // Get all cost centers
  getCostCenters: (params = {}) => financeApi.get('/finance/cost-centers', { params }),

  // Get single cost center
  getCostCenter: (id) => financeApi.get(`/finance/cost-centers/${id}`),

  // Create cost center
  createCostCenter: (data) => financeApi.post('/finance/cost-centers', data),

  // Update cost center
  updateCostCenter: (id, data) => financeApi.put(`/finance/cost-centers/${id}`, data),

  // Delete cost center
  deleteCostCenter: (id) => financeApi.delete(`/finance/cost-centers/${id}`),

  // Toggle status
  toggleStatus: (id, data) => financeApi.patch(`/finance/cost-centers/${id}/toggle-status`, data),

  // Bulk operations
  bulkUpdate: (data) => financeApi.put('/finance/cost-centers/bulk', data),

  // Dropdown list (lightweight)
  getDropdown: (params = {}) => financeApi.get('/finance/cost-centers/dropdown', { params }),

  // Cross-module lookup (auth only — no FINANCE:COST_CENTERS:READ required)
  getCostCentersLookup: (params = {}) => financeApi.get('/finance/cost-centers/lookup', { params }),

  // Hierarchical tree
  getHierarchy: (params = {}) => financeApi.get('/finance/cost-centers/hierarchy', { params }),

  // Static types list
  getTypes: () => financeApi.get('/finance/cost-centers/types'),

  // Preview next suggested code
  previewCode: () => financeApi.post('/finance/cost-centers/preview-code'),

  // Budget (stub)
  getBudget: (id) => financeApi.get(`/finance/cost-centers/${id}/budget`),

  // Actuals (stub)
  getActuals: (id) => financeApi.get(`/finance/cost-centers/${id}/actuals`),

  // ABC costing (stubs)
  configureABC: (costCenterId, data) => financeApi.post(`/finance/cost-centers/abc/${costCenterId}/configure`, data),
  calculateABC: (costCenterId) => financeApi.post(`/finance/cost-centers/abc/${costCenterId}/calculate`),
};

// =============================================================================
// CONTROL ACCOUNTS MANAGEMENT APIs
// =============================================================================

export const controlAccountsApi = {
  // Get all control accounts
  getControlAccounts: (params = {}) => financeApi.get('/finance/control-accounts', { params }),

  // Get control accounts by account type
  getControlAccountsByType: (accountType, params = {}) =>
    financeApi.get(`/finance/control-accounts?accountType=${accountType}`, { params }),

  // Get control accounts by category
  getControlAccountsByCategory: (category, params = {}) =>
    financeApi.get(`/finance/control-accounts?category=${category}`, { params }),

  // Single control account operations
  getControlAccount: (id) => financeApi.get(`/finance/control-accounts/${id}`),
  createControlAccount: (data) => financeApi.post('/finance/control-accounts', data),
  updateControlAccount: (id, data) => financeApi.put(`/finance/control-accounts/${id}`, data),
  deleteControlAccount: (id) => financeApi.delete(`/finance/control-accounts/${id}`),

  // Bulk operations
  bulkUpdateControlAccounts: (operation, accountIds, data = {}) =>
    financeApi.post('/finance/control-accounts/bulk', { operation, accountIds, data }),

  // Preview control account code
  previewControlAccountCode: (data) => financeApi.post('/finance/control-accounts/preview-code', data),

  // Get existing control account types
  getExistingControlAccountTypes: () => financeApi.get('/finance/control-accounts/existing-types'),

  // Control account hierarchy and relationships
  getControlAccountHierarchy: (params = {}) => financeApi.get('/finance/control-accounts/hierarchy', { params }),
  getControlAccountChildren: (id) => financeApi.get(`/finance/control-accounts/${id}/children`),
  getControlAccountParent: (id) => financeApi.get(`/finance/control-accounts/${id}/parent`),
};

// =============================================================================
// ACCOUNTS RECEIVABLE APIs
// =============================================================================

export const receivablesApi = {
  // Customer Management
  getCustomers: (params = {}) => financeApi.get('/finance/receivables/customers', { params }),
  createCustomer: (data) => financeApi.post('/finance/receivables/customers', data),
  getCustomer: (id) => financeApi.get(`/finance/receivables/customers/${id}`),
  updateCustomer: (id, data) => financeApi.put(`/finance/receivables/customers/${id}`, data),
  getCustomerInvoices: (id, params = {}) => financeApi.get(`/finance/receivables/customers/${id}/invoices`, { params }),
  getCustomerAging: (id, params = {}) => financeApi.get(`/finance/receivables/customers/${id}/aging`, { params }),

  // Invoice Management
  getInvoices: (params = {}) => financeApi.get('/finance/receivables/invoices', { params }),
  createInvoice: (data) => financeApi.post('/finance/receivables/invoices', data),
  getInvoice: (id) => financeApi.get(`/finance/receivables/invoices/${id}`),
  sendInvoice: (id, data = {}) => financeApi.post(`/finance/receivables/invoices/${id}/send`, data),
  recordPayment: (id, data) => financeApi.post(`/finance/receivables/invoices/${id}/payments`, data),
  getOverdueInvoices: (params = {}) => financeApi.get('/finance/receivables/invoices/overdue', { params }),

  // Reports
  getAgingReport: (params = {}) => financeApi.get('/finance/receivables/aging-report', { params }),
};

// =============================================================================
// ACCOUNTS PAYABLE APIs
// =============================================================================

export const payablesApi = {
  // Vendor Management
  getVendors: (params = {}) => financeApi.get('/finance/payables/vendors', { params }),
  createVendor: (data) => financeApi.post('/finance/payables/vendors', data),
  getVendor: (id) => financeApi.get(`/finance/payables/vendors/${id}`),
  updateVendor: (id, data) => financeApi.put(`/finance/payables/vendors/${id}`, data),
  getVendorBills: (id, params = {}) => financeApi.get(`/finance/payables/vendors/${id}/bills`, { params }),
  createVendorContract: (id, data) => financeApi.post(`/finance/payables/vendors/${id}/contracts`, data),

  // Bill Management
  getBills: (params = {}) => financeApi.get('/finance/bills', { params }),
  createBill: (data) => financeApi.post('/finance/bills', data),
  getBill: (id) => financeApi.get(`/finance/bills/${id}`),
  updateBill: (id, data) => financeApi.put(`/finance/bills/${id}`, data),
  deleteBill: (id) => financeApi.delete(`/finance/bills/${id}`),
  submitBill: (id) => financeApi.post(`/finance/bills/${id}/submit`),
  hodApproveBill: (id, data = {}) => financeApi.post(`/finance/bills/${id}/hod-approve`, data),
  hodRejectBill: (id, data) => financeApi.post(`/finance/bills/${id}/hod-reject`, data),
  approveBill: (id, data = {}) => financeApi.post(`/finance/bills/${id}/approve`, data),
  rejectBill: (id, data) => financeApi.post(`/finance/bills/${id}/reject`, data),
  getBillSummary: (params = {}) => financeApi.get('/finance/bills/summary', { params }),
  getOverdueBills: (params = {}) => financeApi.get('/finance/bills/overdue', { params }),
  getAccountsPayableAging: (params = {}) => financeApi.get('/finance/bills/reports/accounts-payable-aging', { params }),

  // Bill Line Items
  addLineItem: (billId, data) => financeApi.post(`/finance/bills/${billId}/line-items`, data),
  updateLineItem: (lineItemId, data) => financeApi.put(`/finance/bills/line-items/${lineItemId}`, data),
  deleteLineItem: (lineItemId) => financeApi.delete(`/finance/bills/line-items/${lineItemId}`),

  // Legacy payables endpoints (keep for backward compatibility)
  payBill: (id, data) => financeApi.post(`/finance/payables/bills/${id}/payments`, data),

  // Purchase Orders
  getPurchaseOrders: (params = {}) => financeApi.get('/finance/payables/purchase-orders', { params }),
  createPurchaseOrder: (data) => financeApi.post('/finance/payables/purchase-orders', data),
  threeWayMatch: (id, data) => financeApi.post(`/finance/payables/bills/${id}/three-way-match`, data),
  recordReceipt: (id, data) => financeApi.post(`/finance/payables/purchase-orders/${id}/receipts`, data),

  // Reports & Workflows
  getPendingApprovals: (params = {}) => financeApi.get('/finance/payables/approvals/pending', { params }),
  getUpcomingPayments: (params = {}) => financeApi.get('/finance/payables/payments/upcoming', { params }),
  processRecurringBills: (data = {}) => financeApi.post('/finance/payables/recurring/process', data),
};

// =============================================================================
// BUDGET PLANNING & CONTROL APIs
// =============================================================================

/**
 * Budget Management API Client
 * Handles all budget-related API calls for the Finance Service
 */
export const budgetApi = {
  // =============================================================================
  // BUDGET CRUD OPERATIONS
  // =============================================================================

  /**
   * Get all budgets with filtering and pagination
   * @param {Object} params - Query parameters
   * @returns {Promise} API response with budgets list
   */
  getBudgets: (params = {}) => financeApi.get('/finance/budgets', { params }),

  /**
   * Get single budget by ID
   * @param {string} id - Budget ID
   * @returns {Promise} API response with budget details
   */
  getBudgetById: (id) => financeApi.get(`/finance/budgets/${id}`),

  /**
   * Create new budget
   * @param {Object} budgetData - Budget creation data
   * @returns {Promise} API response with created budget
   */
  createBudget: (budgetData) => financeApi.post('/finance/budgets', budgetData),

  /**
   * Update existing budget
   * @param {string} id - Budget ID
   * @param {Object} budgetData - Budget update data
   * @returns {Promise} API response with updated budget
   */
  updateBudget: (id, budgetData) => financeApi.put(`/finance/budgets/${id}`, budgetData),

  /**
   * Delete budget (soft delete)
   * @param {string} id - Budget ID
   * @returns {Promise} API response
   */
  deleteBudget: (id) => financeApi.delete(`/finance/budgets/${id}`),

  // =============================================================================
  // BUDGET APPROVAL WORKFLOW
  // =============================================================================

  /**
   * Approve budget at specific approval level
   * @param {string} id - Budget ID
   * @param {Object} approvalData - Approval data (order, comments)
   * @returns {Promise} API response
   */
  approveBudget: (id, approvalData) => financeApi.post(`/finance/budgets/${id}/approve`, approvalData),

  /**
   * Reject budget at specific approval level
   * @param {string} id - Budget ID
   * @param {Object} rejectionData - Rejection data (order, comments)
   * @returns {Promise} API response
   */
  rejectBudget: (id, rejectionData) => financeApi.post(`/finance/budgets/${id}/reject`, rejectionData),

  // =============================================================================
  // BUDGET REVISION MANAGEMENT
  // =============================================================================

  /**
   * Create budget revision
   * @param {string} id - Budget ID
   * @param {Object} revisionData - Revision data (reason, changes)
   * @returns {Promise} API response
   */
  createRevision: (id, revisionData) => financeApi.post(`/finance/budgets/${id}/revise`, revisionData),

  /**
   * Get budget revision history
   * @param {string} id - Budget ID
   * @returns {Promise} API response with revision history
   */
  getBudgetRevisions: (id) => financeApi.get(`/finance/budgets/${id}/revisions`),

  // =============================================================================
  // BUDGET ANALYTICS AND REPORTING
  // =============================================================================

  /**
   * Get budgets by period
   * @param {Object} params - Period parameters (startDate, endDate)
   * @returns {Promise} API response with budgets in period
   */
  getBudgetsByPeriod: (params) => financeApi.get('/finance/budgets/by-period', { params }),

  /**
   * Get budgets by cost center
   * @param {string} costCenterId - Cost center ID
   * @returns {Promise} API response with cost center budgets
   */
  getBudgetsByCostCenter: (costCenterId) => financeApi.get(`/finance/budgets/by-cost-center/${costCenterId}`),

  /**
   * Get budget analytics and statistics
   * @param {Object} params - Analytics parameters (period, costCenter)
   * @returns {Promise} API response with analytics data
   */
  getBudgetAnalytics: (params = {}) => financeApi.get('/finance/budgets/analytics', { params }),

  getBudgetVsActualReport: (params = {}) =>
    financeApi.get('/finance/budgets/reports/budget-vs-actual', { params }),

  // =============================================================================
  // BUDGET VALIDATION AND UTILITIES
  // =============================================================================

  /**
   * Submit budget for approval workflow
   * @param {string} id - Budget ID
   * @returns {Promise} API response
   */
  submitBudget: (id) => financeApi.put(`/finance/budgets/${id}`, { status: 'pending_approval' }),

  /**
   * Get budget variance analysis
   * @param {string} id - Budget ID
   * @param {Object} params - Variance parameters
   * @returns {Promise} API response with variance data
   */
  getBudgetVariance: (id, params = {}) => financeApi.get(`/finance/budgets/${id}/variance`, { params }),

  // =============================================================================
  // LEGACY ENDPOINTS (for backward compatibility)
  // =============================================================================

  // Alias for getBudgetById
  getBudget: (id) => financeApi.get(`/finance/budgets/${id}`),

  // Budget Summary and Analysis
  getBudgetSummary: (params = {}) => financeApi.get('/finance/budgets/summary', { params }),
  getPendingApprovals: (params = {}) => financeApi.get('/finance/budgets/approvals/pending', { params }),

  // Budget Templates & Scenarios
  createTemplate: (data) => financeApi.post('/finance/budgets/template', data),
  createScenario: (data) => financeApi.post('/finance/budgets/scenarios', data),
  getScenarios: (params = {}) => financeApi.get('/finance/budgets/scenarios', { params }),
  getScenario: (id) => financeApi.get(`/finance/budgets/scenarios/${id}`),
  updateScenario: (id, data) => financeApi.put(`/finance/budgets/scenarios/${id}`, data),
  analyzeScenario: (id, data = {}) => financeApi.post(`/finance/budgets/scenarios/${id}/analyze`, data),
  getImpactAnalysis: (id, data) => financeApi.post(`/finance/budgets/scenarios/${id}/impact`, data),
  compareScenarios: (data) => financeApi.post('/finance/budgets/scenarios/compare', data),
  sensitivityAnalysis: (id, data) => financeApi.post(`/finance/budgets/scenarios/${id}/sensitivity`, data),
  copyBudget: (id, data) => financeApi.post(`/finance/budgets/${id}/copy`, data),

  // Budget Allocation & Availability Checking
  checkBudgetAvailability: (data) => financeApi.post('/finance/budget-allocations/check-availability', data),
  processTransaction: (data) => financeApi.post('/finance/budget-allocations/process-transaction', data),
  getBudgetAllocations: (params = {}) => financeApi.get('/finance/budget-allocations', { params }),
  getAllocationDetails: (id) => financeApi.get(`/finance/budget-allocations/${id}/details`),
  getDashboardData: (params = {}) => financeApi.get('/finance/budget-allocations/dashboard', { params }),
  getBudgetWarnings: (params = {}) => financeApi.get('/finance/budget-allocations/warnings', { params }),
  getSummaryStatistics: (params = {}) => financeApi.get('/finance/budget-allocations/summary', { params }),
};

// =============================================================================
// COST CENTER MANAGEMENT APIs
// =============================================================================

export const costCenterApi = {
  // Basic CRUD Operations
  getCostCenters: (params = {}) => financeApi.get('/finance/cost-centers', { params }),
  getCostCentersLookup: (params = {}) => financeApi.get('/finance/cost-centers/lookup', { params }),
  getCostCenterById: (id) => financeApi.get(`/finance/cost-centers/${id}`),
  createCostCenter: (data) => financeApi.post('/finance/cost-centers', data),
  updateCostCenter: (id, data) => financeApi.put(`/finance/cost-centers/${id}`, data),
  deleteCostCenter: (id) => financeApi.delete(`/finance/cost-centers/${id}`),

  // Dropdown and Reference Data
  getCostCentersDropdown: (params = {}) => financeApi.get('/finance/cost-centers/dropdown', { params }),
  getCostCenterTypes: () => financeApi.get('/finance/cost-centers/types'),

  // Hierarchy Management
  getCostCenterHierarchy: (params = {}) => financeApi.get('/finance/cost-centers/hierarchy', { params }),

  // Legacy endpoints (for backward compatibility)
  getHierarchy: (params = {}) => financeApi.get('/finance/cost-centers/hierarchy', { params }),
  getCostCenter: (id) => financeApi.get(`/finance/cost-centers/${id}`),
  allocateCosts: (id, data) => financeApi.post(`/finance/cost-centers/${id}/allocate`, data),

  // Budget & Actuals (placeholder for future implementation)
  getCostCenterBudget: (id, params = {}) => financeApi.get(`/finance/cost-centers/${id}/budget`, { params }),
  getCostCenterActuals: (id, params = {}) => financeApi.get(`/finance/cost-centers/${id}/actuals`, { params }),
  getVarianceAnalysis: (id, params = {}) => financeApi.get(`/finance/cost-centers/${id}/variance`, { params }),
  getAllocations: (id, params = {}) => financeApi.get(`/finance/cost-centers/${id}/allocations`, { params }),

  // Reports & Analysis (placeholder for future implementation)
  getSummaryReport: (params = {}) => financeApi.get('/finance/cost-centers/reports/summary', { params }),
  bulkUpdateCostCenters: (data) => financeApi.put('/finance/cost-centers/bulk', data),

  // ABC Costing (legacy placeholder)
  getABCMethods: () => financeApi.get('/finance/cost-centers/abc/methods'),
  configureABC: (costCenterId, data) => financeApi.post(`/finance/cost-centers/abc/${costCenterId}/configure`, data),
  calculateABC: (costCenterId, data = {}) => financeApi.post(`/finance/cost-centers/abc/${costCenterId}/calculate`, data),

  // Inter-Company Charging (legacy placeholder)
  processInterCharging: (data) => financeApi.post('/finance/cost-centers/inter-charging', data),
  getInterChargingReport: (params = {}) => financeApi.get('/finance/cost-centers/inter-charging/report', { params }),
};

// =============================================================================
// PAYMENT PROCESSING APIs
// =============================================================================

export const paymentApi = {
  getPayments: (params = {}) => financeApi.get('/finance/payments/', { params }),
  getPayment: (id) => financeApi.get(`/finance/payments/${id}`),
  createPayment: (data) => financeApi.post('/finance/payments/', data),
  updatePayment: (id, data) => financeApi.put(`/finance/payments/${id}`, data),
  deletePayment: (id) => financeApi.delete(`/finance/payments/${id}`),
  applyPayment: (id, data) => financeApi.post(`/finance/payments/${id}/apply`, data),
  reconcilePayment: (id, data) => financeApi.post(`/finance/payments/${id}/reconcile`, data),
  voidPayment: (id, data = {}) => financeApi.post(`/finance/payments/${id}/void`, data),
  getPaymentSummary: (params = {}) => financeApi.get('/finance/payments/reports/summary', { params }),
  getUnreconciledPayments: (params = {}) => financeApi.get('/finance/payments/unreconciled/list', { params }),
};

// =============================================================================
// FINANCE DASHBOARD API
// =============================================================================

export const dashboardApi = {
  getFinanceDashboard: (params = {}) => financeApi.get('/finance/dashboard', { params }),
};

// =============================================================================
// FINANCIAL REPORTING & ANALYTICS APIs
// =============================================================================

export const reportingApi = {
  // Financial Statements
  getFinancialStatements: (params = {}) => financeApi.get('/finance/reporting/financial-statements', { params }),
  getComparativeStatements: (params = {}) => financeApi.get('/finance/reporting/financial-statements/comparative', { params }),

  // KPI Dashboard
  getKPIDashboard: (params = {}) => financeApi.get('/finance/reporting/kpi-dashboard', { params }),
  getKPIDetails: (kpiId, params = {}) => financeApi.get(`/finance/reporting/kpi-dashboard/${kpiId}`, { params }),

  // Budget Analysis
  getBudgetAnalysis: (params = {}) => financeApi.get('/finance/reporting/budget-analysis', { params }),
  getBudgetVsActual: (params = {}) => financeApi.get('/finance/reporting/budget-analysis/vs-actual', { params }),

  // Custom Reports
  getCustomReports: (params = {}) => financeApi.get('/finance/reporting/custom-reports', { params }),
  createCustomReport: (data) => financeApi.post('/finance/reporting/custom-reports', data),
  executeReport: (reportId, data = {}) => financeApi.post(`/finance/reporting/custom-reports/${reportId}/execute`, data),

  // Compliance Reporting
  getComplianceReports: (params = {}) => financeApi.get('/finance/reporting/compliance-reporting', { params }),
  getOverdueCompliance: (params = {}) => financeApi.get('/finance/reporting/compliance-reporting/overdue', { params }),
  submitComplianceReport: (reportId, data) => financeApi.post(`/finance/reporting/compliance-reporting/${reportId}/submit`, data),
};

// =============================================================================
// CASH & TREASURY MANAGEMENT APIs
// =============================================================================

export const cashManagementApi = {
  // Bank Account Management
  getBankAccounts: (params = {}) => financeApi.get('/finance/cash-management/bank-accounts', { params }),
  getBankAccount: (accountId) => financeApi.get(`/finance/cash-management/bank-accounts/${accountId}`),
  createBankAccount: (data) => financeApi.post('/finance/cash-management/bank-accounts', data),
  updateBankAccount: (accountId, data) => financeApi.put(`/finance/cash-management/bank-accounts/${accountId}`, data),
  getAccountTransactions: (accountId, params = {}) => financeApi.get(`/finance/cash-management/bank-accounts/${accountId}/transactions`, { params }),
  getLowBalanceAlerts: (params = {}) => financeApi.get('/finance/cash-management/bank-accounts/alerts/low-balance', { params }),

  // Cash Receipts
  getCashReceipts: (params = {}) => financeApi.get('/finance/cash-management/cash-receipts', { params }),
  getCashReceipt: (receiptId) => financeApi.get(`/finance/cash-management/cash-receipts/${receiptId}`),
  createCashReceipt: (data) => financeApi.post('/finance/cash-management/cash-receipts', data),
  allocateReceipt: (receiptId, data) => financeApi.post(`/finance/cash-management/cash-receipts/${receiptId}/allocate`, data),
  getUnreconciledReceipts: (params = {}) => financeApi.get('/finance/cash-management/cash-receipts/unreconciled', { params }),

  // Bank Reconciliation
  getBankReconciliations: (params = {}) => financeApi.get('/finance/cash-management/bank-reconciliation', { params }),
  getBankReconciliation: (reconciliationId) => financeApi.get(`/finance/cash-management/bank-reconciliation/${reconciliationId}`),
  startReconciliation: (data) => financeApi.post('/finance/cash-management/bank-reconciliation', data),
  autoMatch: (reconciliationId, data = {}) => financeApi.post(`/finance/cash-management/bank-reconciliation/${reconciliationId}/auto-match`, data),
  addAdjustments: (reconciliationId, data) => financeApi.post(`/finance/cash-management/bank-reconciliation/${reconciliationId}/adjustments`, data),
  completeReconciliation: (reconciliationId, data = {}) => financeApi.post(`/finance/cash-management/bank-reconciliation/${reconciliationId}/complete`, data),

  // Cash Flow Forecasting
  getCashFlowForecasts: (params = {}) => financeApi.get('/finance/cash-management/cash-flow-forecast', { params }),
  getCashFlowForecast: (forecastId) => financeApi.get(`/finance/cash-management/cash-flow-forecast/${forecastId}`),
  createCashFlowForecast: (data) => financeApi.post('/finance/cash-management/cash-flow-forecast', data),
  recalculateForecast: (forecastId, data = {}) => financeApi.post(`/finance/cash-management/cash-flow-forecast/${forecastId}/recalculate`, data),
};

// =============================================================================
// FIXED ASSET MANAGEMENT APIs
// =============================================================================

export const fixedAssetApi = {
  // Asset Management
  getAssets: (params = {}) => financeApi.get('/finance/fixed-assets/', { params }),
  getAsset: (id) => financeApi.get(`/finance/fixed-assets/${id}`),
  createAsset: (data) => financeApi.post('/finance/fixed-assets/', data),
  updateAsset: (id, data) => financeApi.put(`/finance/fixed-assets/${id}`, data),

  // Depreciation
  calculateDepreciation: (data) => financeApi.post('/finance/fixed-assets/depreciation/calculate', data),
  getDepreciationRuns: (params = {}) => financeApi.get('/finance/fixed-assets/depreciation/runs', { params }),

  // Asset Transfers
  createTransfer: (data) => financeApi.post('/finance/fixed-assets/transfers', data),
  approveTransfer: (id, data = {}) => financeApi.put(`/finance/fixed-assets/transfers/${id}/approve`, data),

  // Asset Disposals
  createDisposal: (data) => financeApi.post('/finance/fixed-assets/disposals', data),

  // Maintenance
  getMaintenanceSchedules: (params = {}) => financeApi.get('/finance/fixed-assets/maintenance', { params }),
  createMaintenance: (data) => financeApi.post('/finance/fixed-assets/maintenance', data),
};

// =============================================================================
// BANK MANAGEMENT APIs
// =============================================================================

export const banksApi = {
  // Bank Management
  getBanks: (params = {}) => financeApi.get('/finance/banks', { params }),
  getBank: (id) => financeApi.get(`/finance/banks/${id}`),
  createBank: (data) => financeApi.post('/finance/banks', data),
  updateBank: (id, data) => financeApi.put(`/finance/banks/${id}`, data),
  deleteBank: (id) => financeApi.delete(`/finance/banks/${id}`),
  getBanksDropdown: () => financeApi.get('/finance/banks/dropdown'),

  // Bank Branches nested routes
  getBranchesByBank: (bankId, params = {}) => financeApi.get(`/finance/banks/${bankId}/branches`, { params }),
};

// =============================================================================
// BANK BRANCH MANAGEMENT APIs
// =============================================================================

export const bankBranchesApi = {
  // Branch Management
  getBankBranches: (params = {}) => financeApi.get('/finance/bank-branches', { params }),
  getBankBranch: (id) => financeApi.get(`/finance/bank-branches/${id}`),
  createBankBranch: (data) => financeApi.post('/finance/bank-branches', data),
  updateBankBranch: (id, data) => financeApi.put(`/finance/bank-branches/${id}`, data),
  deleteBankBranch: (id) => financeApi.delete(`/finance/bank-branches/${id}`),
  getBranchesDropdown: (params = {}) => financeApi.get('/finance/bank-branches/dropdown', { params }),
};

// =============================================================================
// CHART OF ACCOUNTS MANAGEMENT APIs
// =============================================================================

export const chartOfAccountsApi = {
  /**
   * Get All Accounts
   * Retrieves a paginated list of chart of accounts with filtering and sorting
   * @param {Object} params - Query parameters
   * @param {string} params.accountTypeId - Filter by account type (ObjectId)
   * @param {string} params.categoryId - Filter by category (ObjectId)
   * @param {string} params.status - Filter by status: 'active', 'inactive', 'all'
   * @param {string} params.search - Search in code, name, or description
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Records per page (default: 50, max: 100)
   * @param {string} params.sortBy - Field to sort by (default: 'code')
   * @param {string} params.sortOrder - Sort order: 'asc' or 'desc' (default: 'asc')
   * @returns {Promise} Response with accounts array and pagination info
   */
  getChartOfAccounts: (params = {}) => financeApi.get('/finance/chart-of-accounts', { params }),
  getAccountStats: () => financeApi.get('/finance/chart-of-accounts/stats'),

  /**
   * Get Account by ID
   * Retrieves detailed information about a specific chart of account
   * @param {string} id - Account ID (ObjectId)
   * @returns {Promise} Response with account details including populated references
   */
  getChartOfAccount: (id) => financeApi.get(`/finance/chart-of-accounts/${id}`),

  /**
   * Create New Account
   * Creates a new chart of account with automatic code generation
   * @param {Object} data - Account data
   * @param {string} data.name - Account name (2-100 chars, required)
   * @param {string} data.description - Account description (max 500 chars, optional)
   * @param {string} data.accountTypeId - Account type reference (ObjectId, required)
   * @param {string} data.categoryId - Category reference (ObjectId, required)
   * @param {string} data.controlAccountId - Control account reference (ObjectId, optional)
   * @param {Object} data.flags - Account type flags
   * @param {boolean} data.flags.regular - Regular account flag (default: true)
   * @param {boolean} data.flags.cashBook - Cash book flag (default: false)
   * @param {boolean} data.flags.bank - Bank account flag (default: false)
   * @param {boolean} data.flags.pettyCash - Petty cash flag (default: false)
   * @param {Object} data.bankInfo - Bank information (required if flags.bank is true)
   * @param {string} data.bankInfo.bankId - Bank reference (ObjectId)
   * @param {string} data.bankInfo.branchId - Branch reference (ObjectId)
   * @param {string} data.bankInfo.accountNumber - Bank account number (5-50 chars)
   * @param {string} data.bankInfo.accountHolderName - Account holder name (2-100 chars)
   * @param {Object} data.pettyCash - Petty cash information (required if flags.pettyCash is true)
   * @param {string} data.pettyCash.cashBookLedgerId - Cash book ledger reference (ObjectId)
   * @param {number} data.pettyCash.reimbursementAmount - Reimbursement amount (min: 0.01)
   * @param {number} data.pettyCash.bufferLevel - Buffer level (min: 0, default: 0)
   * @param {number} data.pettyCash.duration - Duration in days (min: 1, default: 30)
   * @param {string} data.pettyCash.custodian - Custodian name (max: 100 chars, optional)
   * @param {boolean} data.isActive - Active status (default: true)
   * @returns {Promise} Response with created account details
   */
  createChartOfAccount: (data) => financeApi.post('/finance/chart-of-accounts', data),

  /**
   * Update Account
   * Updates an existing chart of account
   * @param {string} id - Account ID (ObjectId)
   * @param {Object} data - Updated account data (same structure as create)
   * @returns {Promise} Response with updated account details
   */
  updateChartOfAccount: (id, data) => financeApi.put(`/finance/chart-of-accounts/${id}`, data),

  /**
   * Delete Account
   * Deletes a chart of account (soft delete if transactions exist)
   * @param {string} id - Account ID (ObjectId)
   * @returns {Promise} Success response
   */
  deleteChartOfAccount: (id) => financeApi.delete(`/finance/chart-of-accounts/${id}`),

  /**
   * Get Accounts Dropdown
   * Retrieves a simplified list of accounts for dropdown/select components
   * @param {Object} params - Query parameters
   * @param {string} params.accountTypeId - Filter by account type
   * @param {string} params.categoryId - Filter by category
   * @param {string} params.status - Filter by status (default: 'active')
   * @returns {Promise} Response with simplified account list (id, code, name only)
   */
  getAccountsDropdown: (params = {}) => financeApi.get('/finance/chart-of-accounts/dropdown', { params }),

  /**
   * Preview Account Code
   * Generates a preview of the account code before creating the account
   * @param {Object} data - Account data for code generation
   * @param {string} data.accountTypeId - Account type reference (ObjectId, required)
   * @param {string} data.categoryId - Category reference (ObjectId, required)
   * @param {string} data.controlAccountId - Control account reference (ObjectId, optional)
   * @returns {Promise} Response with previewed account code
   */
  previewAccountCode: (data) => financeApi.post('/finance/chart-of-accounts/preview-code', data),

  /**
   * Toggle Account Status
   * Toggles account between active and inactive status
   * @param {string} id - Account ID (ObjectId)
   * @param {Object} data - Optional data
   * @param {boolean} data.isActive - Specific status to set (optional, if not provided will toggle current status)
   * @returns {Promise} Response with updated account
   */
  toggleAccountStatus: (id, data = {}) => financeApi.patch(`/finance/chart-of-accounts/${id}/toggle-status`, data),

  // Legacy endpoints (for backward compatibility with old routes)
  // These may be deprecated in future versions
  getAccountsLegacy: (params = {}) => financeApi.get('/finance/accounts', { params }),
  getAccountHierarchy: (params = {}) => financeApi.get('/finance/accounts/hierarchy', { params }),
  getNextAccountCode: (params = {}) => financeApi.get('/finance/accounts/next-code', { params }),
  getAccountTypeRanges: () => financeApi.get('/finance/accounts/type-ranges'),

  /**
   * Bulk Import Chart of Accounts from Excel
   * Extracts Description, Category, AccountCategory columns
   * Automatically creates categories and accounts; test bank created for Bank A/C rows
   * @param {FormData} formData - FormData with 'file' field containing .xls/.xlsx
   * @returns {Promise} Response with summary, createdCategories, createdAccounts
   */
  bulkImportFromExcel: (formData) =>
    financeApi.post('/finance/bulk-import/chart-of-accounts', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0, // no timeout — waits until all rows are processed and response is returned
    }),
};

// =============================================================================
// OPENING BALANCE IMPORT APIs
// =============================================================================

export const openingBalanceImportApi = {
  /**
   * Preview (validate only, no DB writes) an opening balance Excel file
   * @param {FormData} formData - FormData with 'file' field (.xlsx)
   */
  previewImport: (formData) =>
    financeApi.post('/finance/opening-balance-import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    }),

  /**
   * Commit (re-validate + atomically post) an opening balance Excel file
   * @param {FormData} formData - FormData with 'file', 'openingBalanceDate', 'locationName'
   */
  commitImport: (formData) =>
    financeApi.post('/finance/opening-balance-import/commit', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    }),

  getHistory: (params = {}) => financeApi.get('/finance/opening-balance-import/history', { params }),
  getHistoryDetail: (id) => financeApi.get(`/finance/opening-balance-import/history/${id}`),
  downloadHistoryReport: (id) =>
    financeApi.get(`/finance/opening-balance-import/history/${id}/report`, { responseType: 'blob' }),
};

// =============================================================================
// CASH BOOK MANAGEMENT APIs
// =============================================================================

export const cashBookApi = {
  // Get all cash books
  getCashBooks: (params = {}) => financeApi.get('/finance/cashbooks', { params }),

  // Get cash book by ID
  getCashBook: (id) => financeApi.get(`/finance/cashbooks/${id}`),

  // Create new cash book
  createCashBook: (data) => financeApi.post('/finance/cashbooks', data),

  // Update cash book
  updateCashBook: (id, data) => financeApi.put(`/finance/cashbooks/${id}`, data),

  // Delete (soft delete) cash book
  deleteCashBook: (id) => financeApi.delete(`/finance/cashbooks/${id}`),

  // Toggle cash book status
  toggleCashBookStatus: (id, data = {}) => financeApi.patch(`/finance/cashbooks/${id}/toggle-status`, data),

  // Get cash book statistics
  getCashBookStats: () => financeApi.get('/finance/cashbooks/stats'),
};

// =============================================================================
// PETTY CASH CATEGORY MANAGEMENT APIs
// =============================================================================

export const pettyCashCategoryApi = {
  // Get all petty cash categories
  getPettyCashCategories: (params = {}) => financeApi.get('/finance/petty-cash-categories', { params }),

  // Get petty cash category by ID
  getPettyCashCategory: (id) => financeApi.get(`/finance/petty-cash-categories/${id}`),

  // Create new petty cash category
  createPettyCashCategory: (data) => financeApi.post('/finance/petty-cash-categories', data),

  // Update petty cash category
  updatePettyCashCategory: (id, data) => financeApi.put(`/finance/petty-cash-categories/${id}`, data),

  // Delete (soft delete) petty cash category
  deletePettyCashCategory: (id) => financeApi.delete(`/finance/petty-cash-categories/${id}`),

  // Get petty cash category statistics
  getPettyCashCategoryStats: () => financeApi.get('/finance/petty-cash-categories/stats'),
};

// =============================================================================
// ASSET SETTINGS APIs
// =============================================================================

export const assetSettingsApi = {
  // Get global asset settings
  getSettings: () => financeApi.get('/finance/asset-settings'),

  // Update global asset settings
  updateSettings: (data) => financeApi.put('/finance/asset-settings', data),
};

// =============================================================================
// ASSET GROUPS APIs
// =============================================================================

export const assetGroupsApi = {
  // Get all asset groups — params: { isActive: 'true'|'false', search: string }
  getAll: (params = {}) => financeApi.get('/finance/asset-groups', { params }),

  // Get single asset group by ID
  getById: (id) => financeApi.get(`/finance/asset-groups/${id}`),

  // Create new asset group
  create: (data) => financeApi.post('/finance/asset-groups', data),

  // Update existing asset group (code is immutable — backend ignores it)
  update: (id, data) => financeApi.put(`/finance/asset-groups/${id}`, data),

  // Toggle active/inactive status (soft delete / reactivate)
  toggleStatus: (id) => financeApi.patch(`/finance/asset-groups/${id}/toggle-status`),

  // Permanently delete asset group (blocked if assets are linked)
  remove: (id) => financeApi.delete(`/finance/asset-groups/${id}`),
};

// =============================================================================
// ASSET TYPE APIs
// =============================================================================
export const assetTypesApi = {
  // Get all asset types — params: { isActive: 'true'|'false', search: string, assetGroupId: string }
  getAll: (params = {}) => financeApi.get('/finance/asset-types', { params }),

  // Get single asset type by ID
  getById: (id) => financeApi.get(`/finance/asset-types/${id}`),

  // Create new asset type
  create: (data) => financeApi.post('/finance/asset-types', data),

  // Update existing asset type (code is immutable — backend ignores it)
  update: (id, data) => financeApi.put(`/finance/asset-types/${id}`, data),

  // Toggle active/inactive status (soft delete / reactivate)
  toggleStatus: (id) => financeApi.patch(`/finance/asset-types/${id}/toggle-status`),

  // Permanently delete asset type (blocked if assets are linked)
  remove: (id) => financeApi.delete(`/finance/asset-types/${id}`),
};

// =============================================================================
// ASSET CATEGORY APIs
// =============================================================================
export const assetCategoriesApi = {
  getAll: (params = {}) => financeApi.get('/finance/asset-categories', { params }),
  getById: (id) => financeApi.get(`/finance/asset-categories/${id}`),
  create: (data) => financeApi.post('/finance/asset-categories', data),
  update: (id, data) => financeApi.put(`/finance/asset-categories/${id}`, data),
  toggleStatus: (id) => financeApi.patch(`/finance/asset-categories/${id}/toggle-status`),
  remove: (id) => financeApi.delete(`/finance/asset-categories/${id}`),
};

// =============================================================================
// ASSET BRAND APIs
// =============================================================================
export const assetBrandsApi = {
  getAll: (params = {}) => financeApi.get('/finance/asset-brands', { params }),
  getById: (id) => financeApi.get(`/finance/asset-brands/${id}`),
  create: (data) => financeApi.post('/finance/asset-brands', data),
  update: (id, data) => financeApi.put(`/finance/asset-brands/${id}`, data),
  toggleStatus: (id) => financeApi.patch(`/finance/asset-brands/${id}/toggle-status`),
  remove: (id) => financeApi.delete(`/finance/asset-brands/${id}`),
};

// =============================================================================
// ASSET MODEL APIs
// =============================================================================
export const assetModelsApi = {
  getAll: (params = {}) => financeApi.get('/finance/asset-models', { params }),
  getById: (id) => financeApi.get(`/finance/asset-models/${id}`),
  create: (data) => financeApi.post('/finance/asset-models', data),
  update: (id, data) => financeApi.put(`/finance/asset-models/${id}`, data),
  toggleStatus: (id) => financeApi.patch(`/finance/asset-models/${id}/toggle-status`),
  remove: (id) => financeApi.delete(`/finance/asset-models/${id}`),
};

// =============================================================================
// BILL PAYMENT APIs
// =============================================================================

export const billPaymentApi = {
  // Get all bill payments with filters
  getBillPayments: (params = {}) => financeApi.get('/finance/bill-payments', { params }),

  // Get bill payment by ID
  getBillPaymentById: (id) => financeApi.get(`/finance/bill-payments/${id}`),

  // Get pending bills by supplier
  getPendingBillsBySupplier: (supplierId) =>
    financeApi.get(`/finance/bill-payments/pending-bills/${supplierId}`),

  // Get payment summary statistics
  getPaymentSummary: (params = {}) =>
    financeApi.get('/finance/bill-payments/summary', { params }),

  // Create bill payment
  createBillPayment: (data) => financeApi.post('/finance/bill-payments', data),

  // Update bill payment
  updateBillPayment: (id, data) => financeApi.put(`/finance/bill-payments/${id}`, data),

  // Delete bill payment
  deleteBillPayment: (id) => financeApi.delete(`/finance/bill-payments/${id}`),

  // Submit for approval
  submitForApproval: (id) => financeApi.post(`/finance/bill-payments/${id}/submit`),

  // Approve bill payment
  approveBillPayment: (id, data = {}) =>
    financeApi.post(`/finance/bill-payments/${id}/approve`, data),

  // Reject bill payment
  rejectBillPayment: (id, data = {}) =>
    financeApi.post(`/finance/bill-payments/${id}/reject`, data),

  // Post bill payment (create transaction)
  postBillPayment: (id) => financeApi.post(`/finance/bill-payments/${id}/post`),

  // Generate payment voucher
  generatePaymentVoucher: (id) => financeApi.get(`/finance/bill-payments/${id}/voucher`),
};

// Normal Payment API
export const normalPaymentApi = {
  // Get all normal payments with filters
  getNormalPayments: (params = {}) => financeApi.get('/finance/normal-payments', { params }),

  // Get normal payment by ID
  getNormalPaymentById: (id) => financeApi.get(`/finance/normal-payments/${id}`),

  // Get payment statistics
  getPaymentStats: (params = {}) => financeApi.get('/finance/normal-payments/stats', { params }),

  // Create normal payment
  createNormalPayment: (data) => financeApi.post('/finance/normal-payments', data),

  // Update normal payment (DRAFT only)
  updateNormalPayment: (id, data) => financeApi.put(`/finance/normal-payments/${id}`, data),

  // Submit for approval (DRAFT → SUBMITTED)
  submitForApproval: (id, data = {}) => financeApi.post(`/finance/normal-payments/${id}/submit`, data),

  // Approve payment (SUBMITTED → APPROVED)
  approvePayment: (id, data = {}) => financeApi.post(`/finance/normal-payments/${id}/approve`, data),

  // Post payment to general ledger (APPROVED → POSTED)
  postPayment: (id, data = {}) => financeApi.post(`/finance/normal-payments/${id}/post`, data),

  // Reject payment
  rejectPayment: (id, data = {}) => financeApi.post(`/finance/normal-payments/${id}/reject`, data),

  // Delete payment (soft delete - DRAFT only)
  deleteNormalPayment: (id) => financeApi.delete(`/finance/normal-payments/${id}`),

  // Generate payment voucher
  generatePaymentVoucher: (id) => financeApi.get(`/finance/normal-payments/${id}/voucher`),
};

// =============================================================================
// JOURNAL ENTRY MANAGEMENT APIs
// =============================================================================

export const journalEntryApi = {
  // Get all journal entries with pagination and filtering
  getJournalEntries: (params = {}) => financeApi.get('/finance/journal-entries', { params }),

  // Get single journal entry by ID
  getJournalEntry: (id) => financeApi.get(`/finance/journal-entries/${id}`),

  // Create new journal entry
  createJournalEntry: (data) => financeApi.post('/finance/journal-entries', data),

  // Update journal entry (DRAFT only)
  updateJournalEntry: (id, data) => financeApi.put(`/finance/journal-entries/${id}`, data),

  // Delete journal entry (soft delete - DRAFT only)
  deleteJournalEntry: (id) => financeApi.delete(`/finance/journal-entries/${id}`),

  // Workflow operations
  submitJournalEntry: (id, data = {}) => financeApi.post(`/finance/journal-entries/${id}/submit`, data),
  approveJournalEntry: (id, data = {}) => financeApi.post(`/finance/journal-entries/${id}/approve`, data),
  rejectJournalEntry: (id, data = {}) => financeApi.post(`/finance/journal-entries/${id}/reject`, data),

  // Post journal entry to General Ledger
  postJournalEntry: (id, data = {}) => financeApi.post(`/finance/journal-entries/${id}/post`, data),

  // Reverse posted journal entry
  reverseJournalEntry: (id, data = {}) => financeApi.post(`/finance/journal-entries/${id}/reverse`, data),

  // Get dashboard analytics
  getDashboard: (params = {}) => financeApi.get('/finance/journal-entries/dashboard', { params }),

  // Get journal entry statistics
  getStatistics: (params = {}) => financeApi.get('/finance/journal-entries/statistics', { params }),
};

// =============================================================================
// TRANSACTION MANAGEMENT APIs
// =============================================================================

export const transactionsApi = {
  // Get all transactions with filters and pagination
  listTransactions: (params = {}) => financeApi.get('/finance/transactions', { params }),

  // Get transaction by ID
  getTransactionById: (id) => financeApi.get(`/finance/transactions/${id}`),

  // Get transaction lines
  getTransactionLines: (id) => financeApi.get(`/finance/transactions/${id}/lines`),

  // Create new transaction
  createTransaction: (data) => financeApi.post('/finance/transactions', data),

  // Reports
  getAccountBalance: (accountId, params = {}) =>
    financeApi.get(`/finance/transactions/reports/account-balance/${accountId}`, { params }),

  getTrialBalance: (params = {}) =>
    financeApi.get('/finance/transactions/reports/trial-balance', { params }),

  getGeneralLedger: (params = {}) =>
    financeApi.get('/finance/transactions/reports/general-ledger', { params }),

  getModuleRevenue: (params = {}) =>
    financeApi.get('/finance/transactions/reports/module-revenue', { params }),

  getLedgerDetail: (accountId, params = {}) =>
    financeApi.get(`/finance/transactions/reports/ledger-detail/${accountId}`, { params }),

  getStudentStatement: (params = {}) =>
    financeApi.get('/finance/transactions/reports/student-statement', { params }),

  getAccountsReceivableAging: (params = {}) =>
    financeApi.get('/finance/transactions/reports/accounts-receivable-aging', { params }),

  getTWTransactions: (params = {}) =>
    financeApi.get('/finance/transactions/reports/tw-transactions', { params }),

  getExternalCompanyStatement: (params = {}) =>
    financeApi.get('/finance/transactions/reports/external-company-statement', { params }),

  getTransactionSummary: (params = {}) =>
    financeApi.get('/finance/transactions/reports/summary', { params }),

  getIncomeStatement: (params = {}) =>
    financeApi.get('/finance/transactions/reports/income-statement', { params }),

  getBalanceSheet: (params = {}) =>
    financeApi.get('/finance/transactions/reports/balance-sheet', { params }),

  getCashFlowStatement: (params = {}) =>
    financeApi.get('/finance/transactions/reports/cash-flow', { params }),
};

// =============================================================================
// PETTY CASH PAYMENT MANAGEMENT APIs
// =============================================================================

export const pettyCashPaymentApi = {
  // Get all petty cash payments with pagination and filtering
  getPettyCashPayments: (params = {}) => financeApi.get('/finance/petty-cash-payments', { params }),

  // Get single petty cash payment by ID
  getPettyCashPayment: (id) => financeApi.get(`/finance/petty-cash-payments/${id}`),

  // Create new petty cash payment
  createPettyCashPayment: (data) => financeApi.post('/finance/petty-cash-payments', data),

  // Update petty cash payment (DRAFT/REJECTED only)
  updatePettyCashPayment: (id, data) => financeApi.put(`/finance/petty-cash-payments/${id}`, data),

  // Delete petty cash payment (soft delete)
  deletePettyCashPayment: (id) => financeApi.delete(`/finance/petty-cash-payments/${id}`),

  // Workflow operations
  submitForApproval: (id, data = {}) => financeApi.post(`/finance/petty-cash-payments/${id}/submit`, data),
  approvePayment: (id, data = {}) => financeApi.post(`/finance/petty-cash-payments/${id}/approve`, data),
  rejectPayment: (id, data = {}) => financeApi.post(`/finance/petty-cash-payments/${id}/reject`, data),

  // Post payment to General Ledger
  postPayment: (id, data = {}) => financeApi.post(`/finance/petty-cash-payments/${id}/post`, data),

  // Utility operations
  getPendingApprovals: (params = {}) => financeApi.get('/finance/petty-cash-payments/pending-approvals', { params }),
  getNextVoucherNumber: (year) => financeApi.get('/finance/petty-cash-payments/next-voucher-number', { params: { year } }),
  getPaymentSummary: (params = {}) => financeApi.get('/finance/petty-cash-payments/summary', { params }),

  // Bulk operations
  bulkApprove: (data) => financeApi.post('/finance/petty-cash-payments/bulk-approve', data),
};

// =============================================================================
// PETTY CASH FUND MANAGEMENT APIs
// =============================================================================

export const pettyCashApi = {
  // Get all petty cash funds
  getPettyCashFunds: (params = {}) => financeApi.get('/finance/petty-cash', { params }),

  // Get single petty cash fund by ID
  getPettyCashFund: (id) => financeApi.get(`/finance/petty-cash/${id}`),

  // Create new petty cash fund
  createPettyCashFund: (data) => financeApi.post('/finance/petty-cash', data),

  // Update petty cash fund
  updatePettyCashFund: (id, data) => financeApi.put(`/finance/petty-cash/${id}`, data),

  // Delete petty cash fund
  deletePettyCashFund: (id) => financeApi.delete(`/finance/petty-cash/${id}`),
};

// =============================================================================
// FUND TRANSFER MANAGEMENT APIs
// =============================================================================

export const fundTransferApi = {
  // Get all fund transfers with filters and pagination
  getFundTransfers: (params = {}) => financeApi.get('/finance/fund-transfers', { params }),

  // Get fund transfer by ID
  getFundTransferById: (id) => financeApi.get(`/finance/fund-transfers/${id}`),

  // Create new fund transfer
  createFundTransfer: (data) => financeApi.post('/finance/fund-transfers', data),

  // Update fund transfer
  updateFundTransfer: (id, data) => financeApi.put(`/finance/fund-transfers/${id}`, data),

  // Delete fund transfer
  deleteFundTransfer: (id) => financeApi.delete(`/finance/fund-transfers/${id}`),

  // Submit fund transfer for approval
  submitFundTransfer: (id) => financeApi.post(`/finance/fund-transfers/${id}/submit`),

  // Approve fund transfer
  approveFundTransfer: (id) => financeApi.post(`/finance/fund-transfers/${id}/approve`),

  // Reject fund transfer
  rejectFundTransfer: (id, data) => financeApi.post(`/finance/fund-transfers/${id}/reject`, data),

  // Post fund transfer (create accounting transactions)
  postFundTransfer: (id) => financeApi.post(`/finance/fund-transfers/${id}/post`),

  // Get fund transfer double entries (for posted transfers)
  getFundTransferDoubleEntries: (id) => financeApi.get(`/finance/fund-transfers/${id}/double-entries`),
};

// =============================================================================
// BANK DEPOSIT MANAGEMENT APIs
// =============================================================================

export const bankDepositApi = {
  // Get all bank deposits with filters and pagination
  getAll: (params = {}) => financeApi.get('/finance/bank-deposits', { params }),

  // Get single bank deposit by ID
  getById: (id) => financeApi.get(`/finance/bank-deposits/${id}`),

  // Get deposits by status
  getByStatus: (status, params = {}) => financeApi.get(`/finance/bank-deposits/status/${status}`, { params }),

  // Get uncleared deposits for reconciliation
  getUncleared: (params = {}) => financeApi.get('/finance/bank-deposits/uncleared', { params }),

  // Create new bank deposit
  create: (data) => financeApi.post('/finance/bank-deposits', data),

  // Update bank deposit (DRAFT/REJECTED only)
  update: (id, data) => financeApi.put(`/finance/bank-deposits/${id}`, data),

  // Delete bank deposit (mark as DELETED)
  delete: (id) => financeApi.delete(`/finance/bank-deposits/${id}`),

  // Submit deposit for approval (DRAFT -> PENDING_APPROVAL)
  submitForApproval: (id) => financeApi.patch(`/finance/bank-deposits/${id}/submit`),

  // Approve deposit (PENDING_APPROVAL -> PENDING_CLEARANCE) - Creates transaction
  approve: (id) => financeApi.patch(`/finance/bank-deposits/${id}/approve`),

  // Reject deposit (PENDING_APPROVAL -> REJECTED)
  reject: (id, data) => financeApi.patch(`/finance/bank-deposits/${id}/reject`, data),

  // Mark deposit as cleared (PENDING_CLEARANCE -> CLEARED)
  markCleared: (id, data) => financeApi.patch(`/finance/bank-deposits/${id}/clear`, data),
};

// =============================================================================
// CHEQUE APIs
// Cheques are recorded by external services (e.g. CRM) via POST /api/finance/cheques
// and selected for batch deposit via the Finance Bank Deposit workflow.
// =============================================================================

export const chequeApi = {
  // Get all cheques with optional filters: status, bankId, dateFrom, dateTo, search
  getAll: (params = {}) => financeApi.get('/finance/cheques', { params }),

  // Get only PENDING cheques — used by the Bank Deposit form to select cheques
  getPending: (params = {}) => financeApi.get('/finance/cheques/pending', { params }),

  // Get a single cheque by ID (populated with bank and deposit details)
  getById: (id) => financeApi.get(`/finance/cheques/${id}`),

  // Create a new cheque record
  // body: { chequeNumber, chequeDate, amount, drawerName, bankId, [receivedDate], [description], [sourceReference], [locationId] }
  create: (data) => financeApi.post('/finance/cheques', data),

  // Update a cheque (PENDING status only)
  update: (id, data) => financeApi.put(`/finance/cheques/${id}`, data),

  // Cancel a cheque (soft delete, sets status to CANCELLED — PENDING only)
  delete: (id) => financeApi.delete(`/finance/cheques/${id}`),
};

// =============================================================================
// BANK RECONCILIATION APIs
// =============================================================================

export const bankReconciliationApi = {
  getOpeningBalance: (bankAccountId, periodStart) =>
    financeApi.get('/finance/bank-reconciliation/opening-balance', { params: { bankAccountId, periodStart } }),
};

export const bankStatementApi = {
  getAll:    (params = {}) => financeApi.get('/finance/bank-reconciliation/bank-statements', { params }),
  getById:   (id)          => financeApi.get(`/finance/bank-reconciliation/bank-statements/${id}`),
  create:    (data)        => financeApi.post('/finance/bank-reconciliation/bank-statements', data),
  update:    (id, data)    => financeApi.put(`/finance/bank-reconciliation/bank-statements/${id}`, data),
  delete:    (id)          => financeApi.delete(`/finance/bank-reconciliation/bank-statements/${id}`),
  addLine:   (id, data)    => financeApi.post(`/finance/bank-reconciliation/bank-statements/${id}/lines`, data),
  removeLine:(id, lineId)  => financeApi.delete(`/finance/bank-reconciliation/bank-statements/${id}/lines/${lineId}`),
};

export const reconciliationApi = {
  getAll:            (params = {}) => financeApi.get('/finance/bank-reconciliation/reconciliations', { params }),
  getById:           (id)          => financeApi.get(`/finance/bank-reconciliation/reconciliations/${id}`),
  getWorkspace:      (id)          => financeApi.get(`/finance/bank-reconciliation/reconciliations/${id}/workspace`),
  create:            (data)        => financeApi.post('/finance/bank-reconciliation/reconciliations', data),
  matchItem:         (id, data)    => financeApi.post(`/finance/bank-reconciliation/reconciliations/${id}/match`, data),
  unmatchItem:       (id, matchId) => financeApi.delete(`/finance/bank-reconciliation/reconciliations/${id}/match/${matchId}`),
  addAdjustment:     (id, data)    => financeApi.post(`/finance/bank-reconciliation/reconciliations/${id}/adjustments`, data),
  removeAdjustment:  (id, adjId)   => financeApi.delete(`/finance/bank-reconciliation/reconciliations/${id}/adjustments/${adjId}`),
  complete:          (id)          => financeApi.patch(`/finance/bank-reconciliation/reconciliations/${id}/complete`),
  saveLines:         (id, data)    => financeApi.patch(`/finance/bank-reconciliation/reconciliations/${id}/lines`, data),
  delete:            (id)          => financeApi.delete(`/finance/bank-reconciliation/reconciliations/${id}`),
};

// =============================================================================
// PETTY CASH REIMBURSEMENT APIs
// =============================================================================

export const pettyCashReimbursementApi = {
  // Get all reimbursements with filtering and pagination
  getReimbursements: (params = {}) => financeApi.get('/finance/petty-cash-reimbursements', { params }),

  // Get single reimbursement by ID
  getReimbursement: (id) => financeApi.get(`/finance/petty-cash-reimbursements/${id}`),

  // Create new reimbursement
  createReimbursement: (data) => financeApi.post('/finance/petty-cash-reimbursements', data),

  // Update reimbursement (DRAFT/REJECTED only)
  updateReimbursement: (id, data) => financeApi.put(`/finance/petty-cash-reimbursements/${id}`, data),

  // Delete reimbursement (mark as DELETED)
  deleteReimbursement: (id) => financeApi.delete(`/finance/petty-cash-reimbursements/${id}`),

  // Submit reimbursement for approval (DRAFT -> PENDING_APPROVAL)
  submitForApproval: (id) => financeApi.patch(`/finance/petty-cash-reimbursements/${id}/submit`),

  // Approve reimbursement (PENDING_APPROVAL -> APPROVED)
  approveReimbursement: (id) => financeApi.patch(`/finance/petty-cash-reimbursements/${id}/approve`),

  // Reject reimbursement (PENDING_APPROVAL -> REJECTED)
  rejectReimbursement: (id, data) => financeApi.patch(`/finance/petty-cash-reimbursements/${id}/reject`, data),

  // Post reimbursement (APPROVED -> POSTED) - Creates transaction and updates fund
  postReimbursement: (id) => financeApi.patch(`/finance/petty-cash-reimbursements/${id}/post`),

  // Get reimbursements by status
  getReimbursementsByStatus: (status) => financeApi.get(`/finance/petty-cash-reimbursements/status/${status}`),

  // Create reimbursement from petty cash fund
  createFromFund: (fundId, data) => financeApi.post(`/finance/petty-cash/${fundId}/create-reimbursement`, data),
};

// =============================================================================
// ASSET RECORD APIs (L6 — Individual Physical Assets)
// =============================================================================
export const assetsApi = {
  getAll:       (params = {}) => financeApi.get('/finance/assets', { params }),
  getById:      (id)          => financeApi.get(`/finance/assets/${id}`),
  create:       (data)        => financeApi.post('/finance/assets', data),
  update:       (id, data)    => financeApi.put(`/finance/assets/${id}`, data),
  changeStatus: (id, data)    => financeApi.patch(`/finance/assets/${id}/status`, data),
  remove:       (id)          => financeApi.delete(`/finance/assets/${id}`),
};

// ASSET ALLOCATION APIs
// =============================================================================
export const assetAllocationsApi = {
  getAll:      (params = {})  => financeApi.get('/finance/asset-allocations', { params }),
  getById:     (id)           => financeApi.get(`/finance/asset-allocations/${id}`),
  getByAsset:  (assetId)      => financeApi.get(`/finance/asset-allocations/by-asset/${assetId}`),
  create:      (data)         => financeApi.post('/finance/asset-allocations', data),
  returnAsset: (id, data)     => financeApi.patch(`/finance/asset-allocations/${id}/return`, data),
  remove:      (id)           => financeApi.delete(`/finance/asset-allocations/${id}`),
};

export const assetDepreciationApi = {
  preview: (data)         => financeApi.post('/finance/asset-depreciation/preview', data),
  getAll:  (params = {})  => financeApi.get('/finance/asset-depreciation', { params }),
  getById: (id)           => financeApi.get(`/finance/asset-depreciation/${id}`),
  create:  (data)         => financeApi.post('/finance/asset-depreciation', data),
  post:    (id)           => financeApi.patch(`/finance/asset-depreciation/${id}/post`),
  cancel:  (id)           => financeApi.patch(`/finance/asset-depreciation/${id}/cancel`),
  remove:  (id)           => financeApi.delete(`/finance/asset-depreciation/${id}`),
};

// ASSET REPORTS APIs
// =============================================================================
export const assetReportsApi = {
  getDepreciationSummary: (params = {}) => financeApi.get('/finance/asset-reports/reports/depreciation-summary', { params }),
  getHistory:             (params = {}) => financeApi.get('/finance/asset-reports/reports/history', { params }),
  getRegister:            (params = {}) => financeApi.get('/finance/asset-reports/reports/register', { params }),
  getFullyDepreciated:    (params = {}) => financeApi.get('/finance/asset-reports/reports/fully-depreciated', { params }),
};

// =============================================================================
// FINANCE PAYMENT SETTINGS APIs
// =============================================================================

/**
 * Singleton settings that map each inventory-related transaction type to its
 * default counter/offset GL account (the "other side" of the double entry).
 * Covers: GRN, RECEIPT, INVOICE, SUPPLIER_PAYMENT
 */
export const transactionLedgerMappingApi = {
  // GET  /api/finance/transactions/settings/ledger-mapping
  getSettings: () => financeApi.get('/finance/transactions/settings/ledger-mapping'),

  // PUT  /api/finance/transactions/settings/ledger-mapping
  // Body: { mappings: [{ transactionType, isActive, counterLedgerAccountId, counterLineType, label, description }] }
  updateSettings: (data) => financeApi.put('/finance/transactions/settings/ledger-mapping', data),
};

// =============================================================================

/**
 * Singleton settings mapping each CRM payment method to Finance accounts.
 * Finance admin uses this to configure which Chart of Accounts entries are
 * used when CRM auto-creates Bank Deposit records for student payments.
 */
export const paymentSettingsApi = {
  // GET  /api/finance/payment-settings
  // Returns the singleton document (auto-created with defaults if missing)
  // Pass { skipErrorToast: true } for optional CRM COA prefills (callers catch failures).
  getSettings: (config = {}) => financeApi.get('/finance/payment-settings', config),

  // PUT  /api/finance/payment-settings
  // Partial update - only provided fields are modified.
  // Body: { revenueAccountId?, locationId?, locationName?, locationCode?, paymentMethodMappings?: [{ method, sourceAccountId, destinationBankId, costCenterId }] }
  updateSettings: (data) => financeApi.put('/finance/payment-settings', data),
};

// =============================================================================
// FINANCE GENERAL SETTINGS APIs (SUPER_ADMIN only)
// =============================================================================

/**
 * System-wide finance configuration (SUPER_ADMIN only).
 * Controls whether data is filtered by location or loaded for all locations,
 * and whether budget-availability validation is enforced.
 */
export const generalSettingsApi = {
  // GET  /api/finance/general-settings
  getSettings: () => financeApi.get('/finance/general-settings'),

  // PUT  /api/finance/general-settings
  // Body: { showAllLocations?: boolean, locationBypassRoles?: string[], enableBudgetValidation?: boolean }
  updateSettings: (data) => financeApi.put('/finance/general-settings', data),
};

// =============================================================================
// TAX INVOICE NUMBER SETTINGS APIs (SUPER_ADMIN / FINANCE_ADMIN)
// =============================================================================

export const taxInvoiceNumberApi = {
  getSettings: () => financeApi.get('/finance/tax-invoice-numbers/settings'),
  updateSettings: (data) => financeApi.put('/finance/tax-invoice-numbers/settings', data),
  previewNext: (params = {}) => financeApi.get('/finance/tax-invoice-numbers/preview', { params }),
  listRegistry: (params = {}) => financeApi.get('/finance/tax-invoice-numbers/registry', { params }),
};

// =============================================================================
// TAX CONFIGURATION APIs
// =============================================================================

export const taxConfigApi = {
  getTaxConfigs: (params = {}) => financeApi.get('/finance/tax-configs', { params }),
  getTaxConfigById: (id) => financeApi.get(`/finance/tax-configs/${id}`),
  createTaxConfig: (data) => financeApi.post('/finance/tax-configs', data),
  updateTaxConfig: (id, data) => financeApi.put(`/finance/tax-configs/${id}`, data),
  deleteTaxConfig: (id) => financeApi.delete(`/finance/tax-configs/${id}`),
  getPublicTaxConfigs: () => financeApi.get('/finance/tax-configs/public'),
  getPublicEventTaxConfigs: () => financeApi.get('/finance/tax-configs/public/event'),
  getPublicTwTaxConfigs: () => financeApi.get('/finance/tax-configs/public/tw'),
};

export const reportsApi = {
  getBillReport: (id, data) =>
    financeApi.post(`/finance/reports/bills/${id}`, data),
  getBudgetReport: (id, data) =>
    financeApi.post(`/finance/reports/budgets/${id}`, data),
  getBillPaymentReport: (id, data) =>
    financeApi.post(`/finance/reports/bill-payments/${id}`, data),
  getNormalPaymentReport: (id, data) =>
    financeApi.post(`/finance/reports/normal-payments/${id}`, data),
  getJournalEntryReport: (id, data) =>
    financeApi.post(`/finance/reports/journal-entries/${id}`, data),
  getPettyCashPaymentReport: (id, data) =>
    financeApi.post(`/finance/reports/petty-cash-payments/${id}`, data),
  getBankReconciliationReport: (id, data) =>
    financeApi.post(`/finance/reports/bank-reconciliations/${id}`, data),
  getAuditLogs: (params) => financeApi.get('/finance/reports/audit-logs', { params }),
  exportAuditLogs: (payload) =>
    financeApi.post('/finance/reports/audit-logs/export', payload, { responseType: 'blob' }),
  getChequeReport: (sourceType, id, data) =>
    financeApi.post(`/finance/reports/cheque/${sourceType}/${id}`, data),
  getChequeTestReport: (data) =>
    financeApi.post('/finance/reports/cheque/test', data),
  getChequePrintHistory: (sourceType, id, methodIndex) =>
    financeApi.get(`/finance/reports/cheque/${sourceType}/${id}/print-history`, { params: { methodIndex } }),
};

// =============================================================================
// CHEQUE PRINT SETTINGS APIs
// =============================================================================

export const chequePrintSettingsApi = {
  // GET /api/finance/cheque-print-settings
  getSettings: () => financeApi.get('/finance/cheque-print-settings'),

  // PUT /api/finance/cheque-print-settings/global-offset
  updateGlobalOffset: (data) => financeApi.put('/finance/cheque-print-settings/global-offset', data),

  // PUT /api/finance/cheque-print-settings/layout
  // Body: { bankAccountId, leafWidthMm, leafHeightMm, fields }
  saveLayout: (data) => financeApi.put('/finance/cheque-print-settings/layout', data),
};

// External Companies (Finance Service)
export const getEventExternalCompanies = (params = {}) => financeApi.get('/finance/external-companies', { params });
export const getExternalCompanies = (params = {}) => financeApi.get('/finance/external-companies', { params });

export default financeApi;


